// Developer: Ankit Gupta
/**
 * One i18next instance for the whole app, English and Arabic.
 *
 * Translations live in `locales/<lang>/<namespace>.json` and are picked up by
 * the glob below, so a feature adds a namespace by adding two files and never
 * edits this module. Every Insight surface uses its own namespace (`insight`,
 * `insightAgent`, `catalogue`, `documents`) so that parallel work on separate
 * screens does not contend for one giant file.
 *
 * Direction is part of the language and is stamped on <html> (`lang` and
 * `dir`), and App.tsx hands the same direction to antd's ConfigProvider so the
 * drawers, dropdowns and popovers antd renders into document.body follow it.
 * Every screen is translated, so the whole product turns at once.
 *
 * The language changes what a person reads, never what the product writes.
 * Digits stay Latin in Arabic (utils/format.ts), dayjs stays English so a date
 * typed into a picker is sent in the format the server and Fusion expect, and
 * styles/rtl.css keeps code, SQL, identifiers and data cells in their own
 * direction so a value reads the same characters in the same order.
 */
import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { setMetricLanguage, tidyPunctuation } from '@/utils/format';

export type Language = 'en' | 'ar';

export const LANGUAGES: Language[] = ['en', 'ar'];

const STORAGE_KEY = 'datapilot.language';

const modules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/*.json', {
  eager: true,
});

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
for (const [path, module] of Object.entries(modules)) {
  const match = /\.\/locales\/([^/]+)\/([^/]+)\.json$/.exec(path);
  if (!match) continue;
  const [, lang, namespace] = match;
  resources[lang] ??= {};
  resources[lang][namespace] = module.default;
}

function storedLanguage(): Language {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

export function directionOf(language: Language): 'ltr' | 'rtl' {
  return language === 'ar' ? 'rtl' : 'ltr';
}

// Every translated string passes through this. A sentence that ends on an
// interpolated value which already ends in a full stop - the riyal symbol
// "ر.س." is one - would otherwise read "..". Done once here rather than in
// each string, so a sentence written next year cannot bring it back.
i18next.use({
  type: 'postProcessor',
  name: 'tidyPunctuation',
  process: (value: string) => tidyPunctuation(value),
});

void i18next.use(initReactI18next).init({
  resources,
  lng: storedLanguage(),
  fallbackLng: 'en',
  defaultNS: 'insight',
  postProcess: ['tidyPunctuation'],
  interpolation: { escapeValue: false },
  returnNull: false,
  // The resources are bundled, so there is nothing to wait for: initialising
  // synchronously means the first render and every pure formatter (describeOp,
  // describeBound) already see the translations, and no component suspends.
  initAsync: false,
  react: { useSuspense: false },
});
setMetricLanguage(currentLanguageOf(i18next.language));
i18next.on('languageChanged', (language) => setMetricLanguage(currentLanguageOf(language)));

function currentLanguageOf(language: string | undefined): Language {
  return language === 'ar' ? 'ar' : 'en';
}

function stampDocument(language: Language): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = directionOf(language);
}
stampDocument(currentLanguageOf(i18next.language));
i18next.on('languageChanged', (language) => stampDocument(currentLanguageOf(language)));

export function currentLanguage(): Language {
  return i18next.language === 'ar' ? 'ar' : 'en';
}

export async function setLanguage(language: Language): Promise<void> {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // A browser that refuses storage still switches for this session.
  }
  await i18next.changeLanguage(language);
}

/** The interface language, re-rendering the caller when it changes. */
export function useLanguage(): Language {
  const { i18n } = useTranslation();
  return currentLanguageOf(i18n.language);
}

/** `const { t } = useT('insightAgent')`. */
export const useT = (namespace?: string) => useTranslation(namespace);

export default i18next;
